import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Login from './pages/Login/Login';
import Home from './pages/Home/Home';
import LandingPage from './pages/LandingPage/LandingPage';
import { isAuthenticated } from './services/auth';
import EntityTypes from './pages/EntityTypes/EntityTypes';
import Questionnaire from './pages/Questionnaire/Questionnaire';
import AddQuestion from './pages/AddQuestion/AddQuestion';
import QuestionnaireManagement from './pages/QuestionnaireManagement/QuestionnaireManagement';
import ViewQuestionnaire from './pages/ViewQuestionnaire/ViewQuestionnaire';
import UserManagement from './pages/UserManagement/UserManagement';
import { ReviewEntityManagement } from './pages/ReviewEntityManagement/ReviewEntityManagement';
import { ReviewEntityForm } from './pages/ReviewEntityManagement/ReviewEntityForm';
import { ViewReviewEntities } from './pages/ViewReviewEntities/ViewReviewEntities';
import { QuestionnaireResponses } from './pages/QuestionnaireResponses/QuestionnaireResponses';
import ReviewAnswers from './pages/ReviewAnswers/ReviewAnswers';
import ClosedReviews from './pages/ClosedReviews/ClosedReviews';
import ReviewManagement from './pages/ReviewManagement/ReviewManagement';
import ManageMetrics from './pages/Metrics/ManageMetrics';
import ViewMetrics from './pages/Metrics/ViewMetrics';
import MetricsHistory from './pages/Metrics/MetricsHistory';
import ViewMetricsData from './pages/Metrics/ViewMetricsData';
import ViewComputedMetrics from './pages/Metrics/ViewComputedMetrics';
import ViewAllComputedMetrics from './pages/Metrics/ViewAllComputedMetrics';
import EntityAttributes from './pages/EntityAttributes/EntityAttributes';
import ReviewDefinitions from './pages/ReviewDefinitions/ReviewDefinitions';
import { 
    OrganizationSetup, 
    EntityTypeSetup, 
    DimensionsSetup, 
    FrequencySetup,
    MetricLevelSetup
} from './pages/OrganizationSetup';
import MetricThresholds from './pages/OrganizationSetup/MetricThresholds';
import LLMPromptConfigSetup from './pages/OrganizationSetup/LLMPromptConfigSetup';
import ScheduledJobs from './pages/ScheduledJobs/ScheduledJobs';
import Reports from './pages/Reports/Reports';
import ReviewEntitiesByType from './pages/Reports/ReviewEntitiesByType';
import MetricsTrending from './pages/Reports/MetricsTrending';
import Dashboard from './pages/Dashboard';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#2563eb', // Modern blue
            dark: '#1d4ed8',
            light: '#3b82f6',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#64748b', // Slate gray
            dark: '#475569',
            light: '#94a3b8',
            contrastText: '#ffffff',
        },
        success: {
            main: '#10b981', // Emerald
            dark: '#059669',
            light: '#34d399',
        },
        warning: {
            main: '#f59e0b', // Amber
            dark: '#d97706',
            light: '#fbbf24',
        },
        error: {
            main: '#ef4444', // Red
            dark: '#dc2626',
            light: '#f87171',
        },
        info: {
            main: '#06b6d4', // Cyan
            dark: '#0891b2',
            light: '#22d3ee',
        },
        background: {
            default: '#f8fafc',
            paper: '#ffffff',
        },
        text: {
            primary: '#1e293b',
            secondary: '#64748b',
        },
        grey: {
            50: '#f8fafc',
            100: '#f1f5f9',
            200: '#e2e8f0',
            300: '#cbd5e1',
            400: '#94a3b8',
            500: '#64748b',
            600: '#475569',
            700: '#334155',
            800: '#1e293b',
            900: '#0f172a',
        },
    },
    typography: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
        h1: {
            fontFamily: "'Inter', sans-serif",
            fontWeight: 700,
            fontSize: '2.25rem',
            lineHeight: 1.2,
            letterSpacing: '-0.025em',
        },
        h2: {
            fontFamily: "'Inter', sans-serif",
            fontWeight: 700,
            fontSize: '1.875rem',
            lineHeight: 1.3,
            letterSpacing: '-0.025em',
        },
        h3: {
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            fontSize: '1.5rem',
            lineHeight: 1.3,
            letterSpacing: '-0.02em',
        },
        h4: {
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.4,
            letterSpacing: '-0.015em',
        },
        h5: {
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            fontSize: '1.125rem',
            lineHeight: 1.4,
        },
        h6: {
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            fontSize: '1rem',
            lineHeight: 1.5,
        },
        subtitle1: {
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500,
            fontSize: '1rem',
            lineHeight: 1.5,
        },
        subtitle2: {
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500,
            fontSize: '0.875rem',
            lineHeight: 1.4,
        },
        body1: {
            fontFamily: "'Inter', sans-serif",
            fontSize: '1rem',
            lineHeight: 1.6,
        },
        body2: {
            fontFamily: "'Inter', sans-serif",
            fontSize: '0.875rem',
            lineHeight: 1.5,
        },
        button: {
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500,
            textTransform: 'none',
            fontSize: '0.875rem',
            letterSpacing: '0.025em',
        },
        caption: {
            fontFamily: "'Inter', sans-serif",
            fontSize: '0.75rem',
            lineHeight: 1.4,
        },
    },
    shape: {
        borderRadius: 8,
    },
    shadows: [
        'none',
        '0 1px 2px 0 rgb(0 0 0 / 0.05)',
        '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    ],
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    textTransform: 'none',
                    fontWeight: 500,
                    padding: '8px 16px',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px 0 rgb(0 0 0 / 0.15)',
                    },
                },
                contained: {
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                    '&:hover': {
                        boxShadow: '0 4px 12px 0 rgb(0 0 0 / 0.15)',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    border: '1px solid #e2e8f0',
                },
                elevation1: {
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                },
                elevation2: {
                    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                },
                elevation3: {
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 8,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#94a3b8',
                            },
                        },
                        '&.Mui-focused': {
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderWidth: 2,
                            },
                        },
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                        transform: 'translateY(-2px)',
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                    borderBottom: '1px solid #e2e8f0',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        transform: 'scale(1.05)',
                    },
                },
            },
        },
    },
});

const ProtectedRoute = ({ children }: { children: React.ReactElement }) => {
    if (!isAuthenticated()) {
        return <Navigate to="/login" />;
    }
    return children;
};

function App() {
    const [openSessionTimeout, setOpenSessionTimeout] = useState(false);

    useEffect(() => {
        const handleSessionTimeout = () => {
            setOpenSessionTimeout(true);
        };

        window.addEventListener('sessionTimeout', handleSessionTimeout);
        return () => {
            window.removeEventListener('sessionTimeout', handleSessionTimeout);
        };
    }, []);

    const handleClose = () => {
        localStorage.removeItem('token');
        setOpenSessionTimeout(false);
        window.location.href = '/login';
    };

    return (
        <ThemeProvider theme={theme}>
            <BrowserRouter>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route
                        path="/home"
                        element={
                            <ProtectedRoute>
                                <Home />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/entity-types"
                        element={
                            <ProtectedRoute>
                                <EntityTypes />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/review-definitions"
                        element={
                            <ProtectedRoute>
                                <ReviewDefinitions />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/questionnaire/:entityTypeId/:frequency"
                        element={
                            <ProtectedRoute>
                                <Questionnaire />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/add-question"
                        element={
                            <ProtectedRoute>
                                <AddQuestion />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/questionnaire-management"
                        element={
                            <ProtectedRoute>
                                <QuestionnaireManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/view-questionnaire/:reviewDefinitionId/:reviewEntityTypeId"
                        element={
                            <ProtectedRoute>
                                <ViewQuestionnaire />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/manage-metrics"
                        element={
                            <ProtectedRoute>
                                <ManageMetrics />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/view-metrics-data"
                        element={
                            <ProtectedRoute>
                                <ViewMetricsData />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/view-metrics"
                        element={
                            <ProtectedRoute>
                                <ViewMetrics />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/metrics-history"
                        element={
                            <ProtectedRoute>
                                <MetricsHistory />
                            </ProtectedRoute>
                        }
                    />
                    
                    {/* <Route
                        path="/view-computed-metrics/:reviewEntityId"
                        element={
                            <ProtectedRoute>
                                <ViewComputedMetrics />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/view-all-computed-metrics"
                        element={
                            <ProtectedRoute>
                                <ViewAllComputedMetrics />
                            </ProtectedRoute>
                        }
                    /> */}

                    <Route
                    path="/view-computed-metrics"
                    element={
                        <ProtectedRoute>
                        <ViewComputedMetrics />
                        </ProtectedRoute>
                    }
                    />

                    <Route
                        path="/user-management"
                        element={
                            <ProtectedRoute>
                                <UserManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/review-entities"
                        element={
                            <ProtectedRoute>
                                <ReviewEntityManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/review-entities/new"
                        element={
                            <ProtectedRoute>
                                <ReviewEntityForm />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/review-entities/:id"
                        element={
                            <ProtectedRoute>
                                <ReviewEntityForm />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/review-entities/edit/:id"
                        element={
                            <ProtectedRoute>
                                <ReviewEntityForm />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/review-management"
                        element={
                            <ProtectedRoute>
                                <ReviewManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/view-review-entities"
                        element={
                            <ProtectedRoute>
                                <ViewReviewEntities />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/closed-reviews"
                        element={
                            <ProtectedRoute>
                                <ClosedReviews />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/review-answers/:responseId/:reviewEntityTypeId"
                        element={
                            <ProtectedRoute>
                                <ReviewAnswers />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/questionnaire-responses/:entityId/:entityName"
                        element={
                            <ProtectedRoute>
                                <QuestionnaireResponses />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/answer-questionnaire"
                        element={
                            <ProtectedRoute>
                                <ViewReviewEntities />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/review-answers/:responseId/:entityTypeId"
                        element={
                            <ProtectedRoute>
                                <ReviewAnswers />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization-setup"
                        element={
                            <ProtectedRoute>
                                <OrganizationSetup />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization-setup/entity-types"
                        element={
                            <ProtectedRoute>
                                <EntityTypeSetup />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization-setup/metric-levels"
                        element={
                            <ProtectedRoute>
                                <MetricLevelSetup />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization-setup/dimensions"
                        element={
                            <ProtectedRoute>
                                <DimensionsSetup />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization-setup/frequency"
                        element={
                            <ProtectedRoute>
                                <FrequencySetup />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization-setup/metric-thresholds"
                        element={
                            <ProtectedRoute>
                                <MetricThresholds />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization-setup/llm-prompt-config"
                        element={
                            <ProtectedRoute>
                                <LLMPromptConfigSetup />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/entity-attributes"
                        element={
                            <ProtectedRoute>
                                <EntityAttributes />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/scheduled-jobs"
                        element={
                            <ProtectedRoute>
                                <ScheduledJobs />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/reports"
                        element={
                            <ProtectedRoute>
                                <Reports />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/reports/review-entities-by-type"
                        element={
                            <ProtectedRoute>
                                <ReviewEntitiesByType />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/reports/metrics-trending"
                        element={
                            <ProtectedRoute>
                                <MetricsTrending />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <Dashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/" element={<LandingPage />} />
                </Routes>
            </BrowserRouter>
            <Dialog
                open={openSessionTimeout}
                onClose={handleClose}
                aria-labelledby="session-timeout-dialog-title"
                aria-describedby="session-timeout-dialog-description"
            >
                <DialogTitle id="session-timeout-dialog-title">
                    Session Timeout
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="session-timeout-dialog-description">
                        Your session has expired. Please login again.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
}

export default App;